import React, { useEffect, useRef, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  ChevronLeft,
  ChevronRight,
  GraduationCap,
  Pencil,
  Plus,
  Sparkles,
  Trash2,
  Upload,
  X,
  FileText
} from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import PageHeader from "../components/common/PageHeader";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { FieldError, FieldLabel } from "../components/ui/field";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import useToast from "../hooks/useToast";
import { uploadFileToS3 } from "../services/uploadService";
import {
  createCourse,
  deleteCourse,
  getCourses,
  updateCourse
} from "../services/coursesService";

const courseSchema = z.object({
  title: z.string().trim().min(1, "Title is required"),
  short_description: z.string().trim().optional(),
  description: z.string().trim().optional(),
  listing_image: z.string().trim().optional(),
  detail_image: z.string().trim().optional(),
  modes_of_study: z.string().trim().optional(),
  duration: z.string().trim().optional(),
  academic_direction: z.string().trim().optional(),
  start_dates: z.string().trim().optional(),
  fees: z.string().trim().optional(),
  language: z.string().trim().optional(),
  seats_available: z.string().trim().optional(),
  tuition_fees_pdf: z.string().trim().optional()
});

const defaultValues = {
  title: "",
  short_description: "",
  description: "",
  listing_image: "",
  detail_image: "",
  modes_of_study: "",
  duration: "",
  academic_direction: "",
  start_dates: "",
  fees: "",
  language: "",
  seats_available: "",
  tuition_fees_pdf: ""
};

function getInitials(name = "") {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
}

function truncateText(text = "", maxLength = 90) {
  if (!text) {
    return "--";
  }
  return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
}

function getVisiblePageNumbers(currentPage, totalPages) {
  if (totalPages <= 7) return Array.from({ length: totalPages }, (_, index) => index + 1);
  if (currentPage <= 4) return [1, 2, 3, 4, 5, "...", totalPages];
  if (currentPage >= totalPages - 3) return [1, "...", totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
  return [1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages];
}

export default function Courses() {
  const [courses, setCourses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploadingListing, setIsUploadingListing] = useState(false);
  const [isUploadingDetail, setIsUploadingDetail] = useState(false);
  const [isUploadingPdf, setIsUploadingPdf] = useState(false);
  const [isDeletingCourse, setIsDeletingCourse] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCourseId, setEditingCourseId] = useState(null);
  const [coursePendingDelete, setCoursePendingDelete] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 1 });
  
  const fileInputListingRef = useRef(null);
  const fileInputDetailRef = useRef(null);
  const fileInputPdfRef = useRef(null);
  const { showErrorToast, showSuccessToast } = useToast();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting }
  } = useForm({
    defaultValues,
    resolver: zodResolver(courseSchema)
  });

  const listingImage = watch("listing_image");
  const detailImage = watch("detail_image");
  const tuitionFeesPdf = watch("tuition_fees_pdf");
  const descriptionContent = watch("description");

  const refreshListing = (targetPage = pagination.page, targetLimit = pagination.limit) => {
    if (targetPage !== pagination.page || targetLimit !== pagination.limit) {
      setPagination((current) => ({ ...current, page: targetPage, limit: targetLimit }));
      return;
    }
    loadCourses(targetPage, targetLimit);
  };

  async function loadCourses(pageArg = pagination.page, limitArg = pagination.limit) {
    setIsLoading(true);
    const response = await getCourses({ page: pageArg, limit: limitArg });
    if (response?.success) {
      setCourses(response.data.courses || []);
      setPagination({
        page: response.data.pagination?.page || pageArg,
        limit: response.data.pagination?.limit || limitArg,
        total: response.data.pagination?.total || 0,
        totalPages: response.data.pagination?.totalPages || 1
      });
    } else {
      showErrorToast(response?.message || "Unable to load courses");
    }
    setIsLoading(false);
  }

  useEffect(() => {
    loadCourses(pagination.page, pagination.limit);
  }, [pagination.page, pagination.limit]);

  const openCreateModal = () => {
    reset(defaultValues);
    setEditingCourseId(null);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    reset(defaultValues);
    setEditingCourseId(null);
    setIsModalOpen(false);
  };

  const openDeleteModal = (course) => {
    setCoursePendingDelete(course);
  };

  const closeDeleteModal = () => {
    if (isDeletingCourse) return;
    setCoursePendingDelete(null);
  };

  const handleFileUpload = (file, type) => {
    if (!file) return;

    if (type === "pdf") {
      if (file.type !== "application/pdf") {
        showErrorToast("Please upload a PDF file");
        return;
      }
      setIsUploadingPdf(true);
    } else if (type === "listing") {
      if (!file.type.startsWith("image/")) {
        showErrorToast("Please upload an image for listing");
        return;
      }
      setIsUploadingListing(true);
    } else if (type === "detail") {
      if (!file.type.startsWith("image/")) {
        showErrorToast("Please upload an image for detail");
        return;
      }
      setIsUploadingDetail(true);
    }

    uploadFileToS3(file, "courses").then((response) => {
      if (!response?.success) {
        showErrorToast(response?.message || "Unable to upload file");
        if (type === "pdf") setIsUploadingPdf(false);
        if (type === "listing") setIsUploadingListing(false);
        if (type === "detail") setIsUploadingDetail(false);
        return;
      }

      if (type === "pdf") {
        setValue("tuition_fees_pdf", response.data.file.url, { shouldDirty: true, shouldValidate: true });
        setIsUploadingPdf(false);
      } else if (type === "listing") {
        setValue("listing_image", response.data.file.url, { shouldDirty: true, shouldValidate: true });
        setIsUploadingListing(false);
      } else if (type === "detail") {
        setValue("detail_image", response.data.file.url, { shouldDirty: true, shouldValidate: true });
        setIsUploadingDetail(false);
      }
      showSuccessToast("File uploaded successfully");
    });
  };

  const handleEdit = (course) => {
    setEditingCourseId(course.id);
    reset({
      title: course.title,
      short_description: course.short_description || "",
      description: course.description || "",
      listing_image: course.listing_image || "",
      detail_image: course.detail_image || "",
      modes_of_study: course.modes_of_study || "",
      duration: course.duration || "",
      academic_direction: course.academic_direction || "",
      start_dates: course.start_dates || "",
      fees: course.fees || "",
      language: course.language || "",
      seats_available: course.seats_available || "",
      tuition_fees_pdf: course.tuition_fees_pdf || ""
    });
    setIsModalOpen(true);
  };

  const handleDelete = async () => {
    if (!coursePendingDelete) return;
    setIsDeletingCourse(true);
    const response = await deleteCourse(coursePendingDelete.id);
    setIsDeletingCourse(false);

    if (!response?.success) {
      showErrorToast(response?.message || "Unable to delete course");
      return;
    }

    const nextPage = courses.length === 1 && pagination.page > 1 ? pagination.page - 1 : pagination.page;
    showSuccessToast(response.message || "Course deleted successfully");
    closeDeleteModal();
    refreshListing(nextPage, pagination.limit);
  };

  const onSubmit = async (values) => {
    const response = editingCourseId
      ? await updateCourse(editingCourseId, values)
      : await createCourse(values);

    if (!response?.success) {
      showErrorToast(response?.message || "Unable to save course");
      return;
    }

    showSuccessToast(response.message || "Course saved successfully");
    closeModal();
    if (editingCourseId) refreshListing(pagination.page, pagination.limit);
    else refreshListing(1, pagination.limit);
  };

  const startItem = pagination.total ? (pagination.page - 1) * pagination.limit + 1 : 0;
  const endItem = pagination.total ? Math.min(pagination.page * pagination.limit, pagination.total) : 0;
  const pageNumbers = getVisiblePageNumbers(pagination.page, pagination.totalPages);

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Academic Module"
        title="Courses"
        description="Manage courses, study programs, fees, and requirements with rich text description and file uploads."
        badge={`${pagination.total} Courses`}
      />

      <Card className="border-white/90 bg-white/84">
        <CardHeader className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <CardTitle>Course Listing</CardTitle>
            <p className="mt-2 text-sm text-panel-muted">View records and manage entries with paginated control.</p>
          </div>
          <div className="flex items-center gap-3">
            <Button type="button" onClick={openCreateModal}>
              <Plus className="mr-2 h-4 w-4" /> Add Course
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          {isLoading ? (
            <div className="rounded-3xl border p-6 text-sm text-panel-muted">Loading courses...</div>
          ) : courses.length ? (
            <>
              <div className="overflow-hidden rounded-3xl border border-slate-100">
                <div className="overflow-x-auto">
                  <table className="min-w-full border-collapse">
                    <thead className="bg-slate-50/90">
                      <tr className="text-left">
                        <th className="px-5 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-panel-muted">Title</th>
                        <th className="px-5 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-panel-muted">Duration</th>
                        <th className="px-5 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-panel-muted">Fees</th>
                        <th className="px-5 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-panel-muted">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white">
                      {courses.map((course) => (
                        <tr key={course.id} className="border-t border-slate-100 align-top">
                          <td className="px-5 py-4 text-sm font-semibold text-panel-ink">{course.title}</td>
                          <td className="px-5 py-4 text-sm text-panel-muted">{course.duration || "--"}</td>
                          <td className="px-5 py-4 text-sm text-panel-muted">{course.fees || "--"}</td>
                          <td className="px-5 py-4">
                            <div className="flex gap-2">
                              <Button type="button" size="sm" variant="outline" onClick={() => handleEdit(course)}>
                                <Pencil className="mr-2 h-4 w-4" /> Edit
                              </Button>
                              <Button type="button" size="sm" variant="ghost" className="hover:bg-rose-50 hover:text-rose-600 text-panel-muted" onClick={() => openDeleteModal(course)}>
                                <Trash2 className="mr-2 h-4 w-4" /> Delete
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Pagination UI - generic representation identical to Doctor.js */}
              <div className="flex flex-col gap-4 rounded-3xl border border-slate-100 bg-slate-50/70 px-4 py-4 md:flex-row md:items-center md:justify-between">
                <p className="text-sm text-panel-muted">
                  Showing <span className="font-semibold text-panel-ink">{startItem}</span> to <span className="font-semibold text-panel-ink">{endItem}</span> of <span className="font-semibold text-panel-ink">{pagination.total}</span> courses
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  <Button type="button" variant="outline" size="sm" disabled={pagination.page <= 1} onClick={() => setPagination((c) => ({ ...c, page: c.page - 1 }))}>Prev</Button>
                  {pageNumbers.map((item, i) => (
                    item === "..." ? <span key={i} className="px-1 text-sm">...</span> : 
                    <Button key={i} type="button" size="sm" variant={pagination.page === item ? "default" : "outline"} onClick={() => setPagination((c) => ({ ...c, page: item }))}>{item}</Button>
                  ))}
                  <Button type="button" variant="outline" size="sm" disabled={pagination.page >= pagination.totalPages} onClick={() => setPagination((c) => ({ ...c, page: c.page + 1 }))}>Next</Button>
                </div>
              </div>
            </>
          ) : (
            <div className="rounded-3xl border border-dashed border-slate-200 p-8 text-center">No courses found.</div>
          )}
        </CardContent>
      </Card>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 p-4 overflow-y-auto">
          <div className="max-h-[90dvh] w-full max-w-4xl overflow-y-auto rounded-[32px] border bg-white shadow-xl p-6">
            <div className="flex items-center justify-between border-b pb-4 mb-4">
              <h2 className="text-2xl font-bold">{editingCourseId ? "Edit Course" : "Add Course"}</h2>
              <button type="button" onClick={closeModal}><X /></button>
            </div>

            <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
              <div className="space-y-2">
                <FieldLabel>Title *</FieldLabel>
                <Input placeholder="Enter course title" {...register("title")} />
                {errors.title && <FieldError>{errors.title.message}</FieldError>}
              </div>

              <div className="space-y-2">
                <FieldLabel>Short Description</FieldLabel>
                <Textarea placeholder="Short summary" {...register("short_description")} />
              </div>

              <div className="space-y-2 course-editor-wrapper">
                <style>{`
                  .course-editor-wrapper .ql-container {
                    height: 160px;
                    overflow-y: auto;
                  }
                  .course-editor-wrapper .ql-editor {
                    min-height: 160px;
                  }
                `}</style>
                <FieldLabel>Description (Rich Text)</FieldLabel>
                <ReactQuill 
                  theme="snow" 
                  value={descriptionContent || ""} 
                  onChange={(content) => setValue("description", content)} 
                />
              </div>

              <div className="grid grid-cols-2 gap-4 mt-6">
                {/* Image Uploads */}
                <div className="space-y-2 border p-4 rounded-xl">
                  <FieldLabel>Listing Image</FieldLabel>
                  <input ref={fileInputListingRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleFileUpload(e.target.files?.[0], "listing")} />
                  <Button type="button" variant="outline" onClick={() => fileInputListingRef.current?.click()} disabled={isUploadingListing}>
                    {isUploadingListing ? "Uploading..." : "Upload Listing Image"}
                  </Button>
                  {listingImage && <img src={listingImage} alt="Listing" className="h-20 w-20 object-cover mt-2 rounded-lg" />}
                </div>

                <div className="space-y-2 border p-4 rounded-xl">
                  <FieldLabel>Detail Image</FieldLabel>
                  <input ref={fileInputDetailRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleFileUpload(e.target.files?.[0], "detail")} />
                  <Button type="button" variant="outline" onClick={() => fileInputDetailRef.current?.click()} disabled={isUploadingDetail}>
                    {isUploadingDetail ? "Uploading..." : "Upload Detail Image"}
                  </Button>
                  {detailImage && <img src={detailImage} alt="Detail" className="h-20 w-20 object-cover mt-2 rounded-lg" />}
                </div>
              </div>

              {/* Extra Info Fields */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><FieldLabel>Modes of Study</FieldLabel><Input {...register("modes_of_study")} /></div>
                <div className="space-y-2"><FieldLabel>Duration</FieldLabel><Input {...register("duration")} /></div>
                <div className="space-y-2"><FieldLabel>Academic Direction</FieldLabel><Input {...register("academic_direction")} /></div>
                <div className="space-y-2"><FieldLabel>Start Dates</FieldLabel><Input {...register("start_dates")} /></div>
                <div className="space-y-2"><FieldLabel>Fees (First Year)</FieldLabel><Input {...register("fees")} /></div>
                <div className="space-y-2"><FieldLabel>Language</FieldLabel><Input {...register("language")} /></div>
                <div className="space-y-2"><FieldLabel>Seats Available</FieldLabel><Input {...register("seats_available")} /></div>
              </div>

              {/* Strict PDF Upload Form section */}
              <div className="space-y-2 border p-4 rounded-xl bg-slate-50">
                <FieldLabel>Courses Tuition Fees (PDF STRICT)</FieldLabel>
                <input ref={fileInputPdfRef} type="file" accept="application/pdf" className="hidden" onChange={(e) => handleFileUpload(e.target.files?.[0], "pdf")} />
                <div className="flex items-center gap-3">
                  <Button type="button" variant="outline" onClick={() => fileInputPdfRef.current?.click()} disabled={isUploadingPdf}>
                    <Upload className="mr-2 h-4 w-4" /> {isUploadingPdf ? "Uploading..." : "Upload Tuition PDF"}
                  </Button>
                  {tuitionFeesPdf ? (
                    <div className="flex items-center gap-2 text-sm text-primary">
                      <FileText size={16} /> <a href={tuitionFeesPdf} target="_blank" rel="noreferrer" className="underline">View PDF</a>
                    </div>
                  ) : <span className="text-xs text-panel-muted">Only PDF files accepted</span>}
                </div>
              </div>

              <div className="flex gap-3 pt-4 border-t">
                <Button type="submit" disabled={isSubmitting || isUploadingListing || isUploadingDetail || isUploadingPdf}>
                  {isSubmitting ? "Saving..." : editingCourseId ? "Update Course" : "Save Course"}
                </Button>
                <Button type="button" variant="outline" onClick={closeModal}>Cancel</Button>
              </div>
            </form>
          </div>
        </div>
      )}
      {coursePendingDelete && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/50 p-4">
          <div className="w-full max-w-md rounded-[32px] border bg-white p-6 shadow-xl">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-rose-50 text-rose-600">
              <Trash2 className="h-5 w-5" />
            </div>
            <h2 className="mt-5 text-2xl font-bold">Delete Course</h2>
            <p className="mt-3 text-sm text-panel-muted">
              Are you sure you want to delete <span className="font-semibold">{coursePendingDelete.title}</span>?
            </p>
            <div className="mt-6 flex gap-3">
              <Button type="button" variant="destructive" onClick={handleDelete} disabled={isDeletingCourse}>
                {isDeletingCourse ? "Deleting..." : "Delete Course"}
              </Button>
              <Button type="button" variant="outline" onClick={closeDeleteModal} disabled={isDeletingCourse}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
